import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SweetAlertService } from '../../../services/sweet-alert.service';
import { UserService } from '../../../services/user.service';
import {
  PERFIL_COPY,
  PerfilSeccionEstado,
  PerfilSeccionId,
} from '../../data/perfil-microempresario.content';
import { isPerfilJuridica } from '../perfil-tipo.util';
import { PerfilSeccionAccesoService } from '../progress/perfil-seccion-acceso.service';
import {
  PERFIL_ALIANZA_ROUTE,
  PERFIL_NAV_INTRO_SECCION,
  PERFIL_NAV_SHOW_ALIANZA,
  PERFIL_NAV_SHOW_ENVIAR,
  PERFIL_NAV_SHOW_INTRO,
  PERFIL_SECTION_ROUTES,
  PERFIL_SOLICITUD_ENVIADA_ROUTE,
  consumeShowAlianzaFlag,
  consumeShowEnviarFlag,
  consumeShowIntroFlag,
  readPerfilNavState,
} from '../progress/perfil-section-nav';
import { PerfilSegmentoService } from '../segmento/perfil-segmento.service';
import { PerfilSolicitudService } from '../solicitud/perfil-solicitud.service';
import { AlianzaEstrategica } from '../alianza/perfil-alianza.model';
import { PerfilAlianzaService } from '../alianza/perfil-alianza.service';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';

interface HubSeccionView {
  id: PerfilSeccionId;
  titulo: string;
  icon: 'face' | 'store' | 'attach_money';
  estado: PerfilSeccionEstado;
  progreso: number;
  checkpoints: number;
  /** Navegación a la sección; deshabilitada si la anterior no está completa. */
  route: string | null;
  disabled: boolean;
}

function buildHubSeccionesIniciales(
  juridica: boolean
): HubSeccionView[] {
  if (juridica) {
    return [
      {
        id: 'sobre-ti',
        titulo:
          PERFIL_COPY.secciones['sobre-ti'].tituloJuridica ??
          PERFIL_COPY.secciones['sobre-ti'].titulo,
        icon: 'store',
        estado: 'pendiente',
        progreso: 0,
        checkpoints: 3,
        route: PERFIL_SECTION_ROUTES['sobre-ti'],
        disabled: false,
      },
      {
        id: 'sobre-ingresos',
        titulo: PERFIL_COPY.secciones['sobre-ingresos'].titulo,
        icon: 'attach_money',
        estado: 'pendiente',
        progreso: 0,
        checkpoints: 3,
        route: PERFIL_SECTION_ROUTES['sobre-ingresos'],
        disabled: false,
      },
    ];
  }
  return [
    {
      id: 'sobre-ti',
      titulo: PERFIL_COPY.secciones['sobre-ti'].titulo,
      icon: 'face',
      estado: 'pendiente',
      progreso: 0,
      checkpoints: 2,
      route: PERFIL_SECTION_ROUTES['sobre-ti'],
      disabled: false,
    },
    {
      id: 'sobre-negocio',
      titulo: PERFIL_COPY.secciones['sobre-negocio'].titulo,
      icon: 'store',
      estado: 'pendiente',
      progreso: 0,
      checkpoints: 3,
      route: PERFIL_SECTION_ROUTES['sobre-negocio'],
      disabled: false,
    },
    {
      id: 'sobre-ingresos',
      titulo: PERFIL_COPY.secciones['sobre-ingresos'].titulo,
      icon: 'attach_money',
      estado: 'pendiente',
      progreso: 0,
      checkpoints: 3,
      route: PERFIL_SECTION_ROUTES['sobre-ingresos'],
      disabled: false,
    },
  ];
}

@Component({
  selector: 'app-perfil-hub',
  templateUrl: './perfil-hub.component.html',
  styleUrls: ['./perfil-hub.component.scss'],
  standalone: false,
})
export class PerfilHubComponent implements OnInit, OnDestroy {
  readonly copy = PERFIL_COPY;
  userName = '';
  isJuridica = false;

  introModalOpen = false;
  introSeccionId: PerfilSeccionId = 'sobre-ti';
  enviarModalOpen = false;

  /**
   * Enviar solo con las secciones del hub al 100 %.
   * Acceso a secciones: bloqueo secuencial.
   */
  puedeSolicitarCredito = false;
  enviando = false;

  secciones: HubSeccionView[] = buildHubSeccionesIniciales(false);
  alianzas: AlianzaEstrategica[] = [];

  private readonly sub = new Subscription();

  /** Capturado en constructor (getCurrentNavigation). */
  private readonly pendingIntroSeccionId: PerfilSeccionId | null;
  private readonly pendingEnviarModal: boolean;
  private readonly pendingAlianzaLanding: boolean;
  private alianzasListas = false;
  private navPostSaveResuelto = false;

  constructor(
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly segmentoService: PerfilSegmentoService,
    private readonly seccionAcceso: PerfilSeccionAccesoService,
    private readonly solicitudService: PerfilSolicitudService,
    private readonly alianzaService: PerfilAlianzaService,
    private readonly sweetAlert: SweetAlertService,
    private readonly analyticsTag: AnalyticsTagService
  ) {
    const state = readPerfilNavState(this.router);
    const wantsIntro = !!state[PERFIL_NAV_SHOW_INTRO];
    const seccion = state[PERFIL_NAV_INTRO_SECCION];
    this.pendingIntroSeccionId =
      wantsIntro && typeof seccion === 'string'
        ? (seccion as PerfilSeccionId)
        : null;
    this.pendingEnviarModal = !!state[PERFIL_NAV_SHOW_ENVIAR];
    this.pendingAlianzaLanding = !!state[PERFIL_NAV_SHOW_ALIANZA];
  }

  ngOnInit(): void {
    this.userName = this.userService.getUser()?.nombre ?? '';
    this.isJuridica = isPerfilJuridica(this.userService.getUser()?.tipoPersona);
    this.secciones = buildHubSeccionesIniciales(this.isJuridica);
    this.segmentoService.refreshFromDraft();
    this.syncIngresosCheckpoints();
    this.sub.add(
      this.segmentoService.ensureParamsLoaded().subscribe(() => {
        this.segmentoService.refreshFromDraft();
        this.syncIngresosCheckpoints();
      })
    );
    this.sub.add(
      this.seccionAcceso.ensureFromSolicitud().subscribe(() => {
        this.applyFromSolicitud();
        this.maybeOpenIntroFromNav();
        this.maybeOpenAlianzaOrEnviarFromNav();
        this.sub.add(
          this.solicitudService.persistirCamposProgreso().subscribe(() => {
            this.applyFromSolicitud();
          })
        );
      })
    );
    this.applyFromSolicitud();
    this.sub.add(
      this.alianzaService.loadActivas().subscribe((lista) => {
        this.alianzas = lista;
        this.alianzasListas = true;
        this.maybeOpenAlianzaOrEnviarFromNav();
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get progresoGlobal(): number {
    if (!this.secciones.length) {
      return 0;
    }
    return Math.round(
      this.secciones.reduce((acc, s) => acc + s.progreso, 0) /
        this.secciones.length
    );
  }

  get seccionesCompletas(): number {
    return this.secciones.filter((s) => s.progreso >= 100).length;
  }

  /** Las 3 secciones al 100 %: el CTA «Completa tu perfil» ya no aplica. */
  get perfilCompleto(): boolean {
    return this.seccionesCompletas >= this.secciones.length;
  }

  /** Vacío PJ = title PN; con progreso = titleJuridica (Figma). */
  get hubTitle(): string {
    if (!this.isJuridica) {
      return this.copy.hub.title;
    }
    return this.progresoGlobal > 0
      ? this.copy.hub.titleJuridica ?? this.copy.hub.title
      : this.copy.hub.title;
  }

  get ctaLabel(): string {
    return this.puedeSolicitarCredito
      ? this.copy.hub.ctaEnviarSolicitud
      : this.copy.hub.ctaSolicitarCredito;
  }

  get segmentoIntro() {
    return this.segmentoService.segmento;
  }

  /** Acción hub según estado: Empezar / Continuar / Editar. */
  actionLabelFor(seccion: HubSeccionView): string {
    if (seccion.estado === 'completo' || seccion.progreso >= 100) {
      return this.copy.acciones.editar;
    }
    if (seccion.progreso > 0) {
      return this.copy.acciones.retomar;
    }
    return this.copy.acciones.empezar;
  }

  onSeccionAction(seccion: HubSeccionView): void {
    if (seccion.disabled) {
      return;
    }
    this.analyticsTag.trackCtaClick({
      ctaLabel: this.actionLabelFor(seccion),
      ctaSection: 'hub',
      ctaComponent: 'perfil-section-card',
      ctaElementId: `btn-perfil-hub-${seccion.id}`,
      hu: '8099',
    });
    this.openSeccionOrIntro(seccion);
  }

  /**
   * CTA solicitudes (Figma): intro solo si la sección está en 0 %;
   * con progreso > 0 navega directo.
   */
  onCompletarPerfil(): void {
    if (this.perfilCompleto) {
      return;
    }
    this.analyticsTag.trackCtaClick({
      ctaLabel: this.copy.hub.ctaCompletarPerfil,
      ctaSection: 'hub',
      ctaComponent: 'perfil-hub',
      ctaElementId: 'btn-perfil-completar-solicitudes',
      hu: '8099',
    });
    const primera =
      this.secciones.find((s) => !s.disabled && s.estado !== 'completo') ??
      this.secciones.find((s) => !s.disabled) ??
      this.secciones[0];
    if (primera) {
      this.openSeccionOrIntro(primera);
    }
  }

  /** Intro solo una vez por sección y solicitud, aunque existan datos precargados. */
  private openSeccionOrIntro(seccion: HubSeccionView): void {
    if (this.seccionAcceso.shouldShowIntro(seccion.id)) {
      this.openIntroModal(seccion.id);
      return;
    }
    if (seccion.route) {
      void this.router.navigateByUrl(seccion.route);
    }
  }

  openIntroModal(seccionId: PerfilSeccionId): void {
    this.seccionAcceso.markIntroShown(seccionId);
    this.introSeccionId = seccionId;
    this.introModalOpen = true;
  }

  closeIntroModal(): void {
    this.introModalOpen = false;
  }

  onIntroCompletarDatos(seccionId: PerfilSeccionId): void {
    const seccion = this.secciones.find((s) => s.id === seccionId);
    this.closeIntroModal();
    if (seccion?.route) {
      void this.router.navigateByUrl(seccion.route);
    }
  }

  /**
   * CTA saludo: con perfil al 100 % abre el modal de envío.
   */
  onSolicitarCredito(): void {
    if (!this.puedeSolicitarCredito) {
      return;
    }
    this.analyticsTag.trackCtaClick({
      ctaLabel: this.ctaLabel,
      ctaSection: 'hub',
      ctaComponent: 'perfil-hub',
      ctaElementId: 'btn-perfil-solicitar-credito',
      hu: '8099',
    });
    this.openEnviarModal();
  }

  openEnviarModal(): void {
    this.enviarModalOpen = true;
  }

  closeEnviarModal(): void {
    this.enviarModalOpen = false;
  }

  onEnviarRevisar(): void {
    this.closeEnviarModal();
  }

  onEnviarSolicitud(): void {
    if (this.enviando) {
      return;
    }
    this.enviando = true;
    this.sub.add(
      this.solicitudService.validarRegistroParaEnvio().subscribe({
        next: (registro) => {
          if (!registro.ok) {
            this.enviando = false;
            void this.sweetAlert
              .infoModal(this.copy.modalEnviar.registroIncompleto, false)
              .then((result) => {
                if (result.isConfirmed) {
                  void this.router.navigate(['/microempresario/actualizar-datos']);
                }
              });
            return;
          }
          this.sub.add(
            this.solicitudService.validarDocumentosParaEnvio().subscribe({
              next: (check) => {
                if (!check.ok) {
                  this.enviando = false;
                  void this.sweetAlert.errorModal(
                    'Error!',
                    true,
                    'Los documentos cargados presentan inconsistencias, por favor revise nuevamente'
                  );
                  return;
                }
                this.sub.add(
                  this.solicitudService.enviarSolicitud().subscribe((res) => {
                    this.enviando = false;
                    if (!res.exitoso) {
                      void this.sweetAlert.errorModal(
                        'Error!',
                        true,
                        res.error || 'No se pudo enviar la solicitud'
                      );
                      return;
                    }
                    this.closeEnviarModal();
                    const id = res.idSolicitudCredito;
                    if (id) {
                      void this.router.navigate([
                        PERFIL_SOLICITUD_ENVIADA_ROUTE,
                        id,
                      ]);
                    } else {
                      void this.router.navigateByUrl(
                        PERFIL_SOLICITUD_ENVIADA_ROUTE
                      );
                    }
                  })
                );
              },
              error: () => {
                this.enviando = false;
                void this.sweetAlert.errorModal(
                  'Error!',
                  true,
                  'No se pudo validar los documentos. Intenta de nuevo.'
                );
              },
            })
          );
        },
        error: () => {
          this.enviando = false;
          void this.sweetAlert.errorModal(
            'Error!',
            true,
            'No se pudo validar el registro. Intenta de nuevo.'
          );
        },
      })
    );
  }

  /** Llegada desde una sección previa: respeta el control una vez por solicitud. */
  private maybeOpenIntroFromNav(): void {
    const seccionId = this.pendingIntroSeccionId;
    if (!seccionId) {
      return;
    }
    if (!this.seccionAcceso.shouldShowIntro(seccionId)) {
      consumeShowIntroFlag(readPerfilNavState(this.router));
      return;
    }
    this.openIntroModal(seccionId);
    consumeShowIntroFlag(readPerfilNavState(this.router));
  }

  /**
   * Al completar la última sección: landing si hay alianzas;
   * si no, el modal de envío.
   */
  private maybeOpenAlianzaOrEnviarFromNav(): void {
    if (this.navPostSaveResuelto || !this.alianzasListas) {
      return;
    }
    if (this.pendingAlianzaLanding) {
      consumeShowAlianzaFlag(readPerfilNavState(this.router));
      if (this.alianzas.length) {
        this.navPostSaveResuelto = true;
        consumeShowEnviarFlag(readPerfilNavState(this.router));
        void this.router.navigateByUrl(PERFIL_ALIANZA_ROUTE);
        return;
      }
    }
    this.navPostSaveResuelto = true;
    this.maybeOpenEnviarFromNav();
  }

  /** Tras completar la última sección: abrir modal de envío en el hub. */
  private maybeOpenEnviarFromNav(): void {
    if (!this.pendingEnviarModal) {
      return;
    }
    consumeShowEnviarFlag(readPerfilNavState(this.router));
    if (this.puedeSolicitarCredito) {
      this.openEnviarModal();
    }
  }

  /** Express sin flujo: 2 checkpoints; con flujo / tradicional / PJ: 3. */
  private syncIngresosCheckpoints(): void {
    if (this.isJuridica) {
      this.secciones = this.secciones.map((s) =>
        s.id === 'sobre-ingresos' ? { ...s, checkpoints: 3 } : s
      );
      return;
    }
    const checkpoints = this.segmentoService.muestraFlujoCaja ? 3 : 2;
    this.secciones = this.secciones.map((s) =>
      s.id === 'sobre-ingresos' ? { ...s, checkpoints } : s
    );
  }

  private applyFromSolicitud(): void {
    const ingresosCheckpoints = this.isJuridica
      ? 3
      : this.segmentoService.muestraFlujoCaja
        ? 3
        : 2;
    const order = this.seccionAcceso.sectionOrder();
    const percents = new Map(
      order.map((id) => [id, this.seccionAcceso.sectionPercent(id)] as const)
    );

    this.secciones = this.secciones.map((card) => {
      const progreso = percents.get(card.id) ?? 0;
      const estado: PerfilSeccionEstado =
        progreso >= 100
          ? 'completo'
          : progreso > 0
            ? 'en_proceso'
            : 'pendiente';
      return {
        ...card,
        progreso,
        checkpoints:
          card.id === 'sobre-ingresos' ? ingresosCheckpoints : card.checkpoints,
        estado,
      };
    });

    this.secciones = this.secciones.map((card) => {
      const idx = order.indexOf(card.id);
      let disabled = false;
      if (idx > 0) {
        // Misma regla que PerfilSeccionAccesoService.canAccess
        disabled = !this.seccionAcceso.canAccess(card.id);
      }
      return { ...card, disabled };
    });

    this.puedeSolicitarCredito =
      this.seccionesCompletas >= this.secciones.length;
  }
}
